import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { isEqual } from "lodash";
import qs from "qs";
import { isDateTime } from "@cpt/shared";

interface UseQueryParamProps {
  paramKeys: string[];
}

type UseQueryParamModel = [
  parameters: Record<string, string>,
  setParameters: (updatedParams: Record<string, unknown>) => void
];

function useQueryParams(props: UseQueryParamProps): UseQueryParamModel {
  const { paramKeys } = props;
  const [searchParams, setSearchParams] = useSearchParams();

  const parsedQueryString = useMemo(() => {
    return qs.parse(searchParams.toString(), { allowDots: true });
  }, [searchParams]);

  const parameters = useMemo(() => {
    return paramKeys.reduce((acc: Record<string, unknown>, curr: string) => {
      const value = parsedQueryString[curr];

      if (value) {
        acc[curr] = value;
      }

      return acc;
    }, {});
  }, [paramKeys, parsedQueryString]);

  const setParameters = useCallback(
    (updatedParams: Record<string, unknown>) => {
      if (isEqual(updatedParams, parameters)) {
        return;
      }

      const parsedUpdated = paramKeys.reduce((acc: Record<string, unknown>, curr) => {
        const updatedValue = updatedParams[curr];

        if (isDateTime(updatedValue)) {
          acc[curr] = updatedValue.toUTC().toISODate();
        } else {
          acc[curr] = updatedValue;
        }

        return acc;
      }, {});

      const queryString = qs.stringify(
        { ...parsedQueryString, ...parsedUpdated },
        { allowDots: true, skipNulls: true }
      );
      setSearchParams(queryString, { replace: true });
    },
    [parsedQueryString, paramKeys, parameters, setSearchParams]
  );

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  return [parameters, setParameters];
}

export default useQueryParams;
